/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
import IconDelete from '@assets/icons/delete.svg?react';
import IconPlus from '@assets/icons/plus.svg?react';
import {
 Box,
 CircularProgress,
 IconButton,
 Typography,
 useTheme,
} from '@mui/material';
import { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useAuthContext } from '@/contexts/AuthContext';
import { useConfirmationDialog } from '../../../contexts/useConfirmationDialog';
import { useAppDispatch } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import blobFile from '../../../utils/blobFile';
import { titleStyles } from './infoStyles';
import { downloadBase64File } from '../../../utils/base64File';

const InfoDocuments = ({ id, table }: { id: number; table: string }) => {
 const { t } = useTranslation();
 const dispatch = useAppDispatch();
 const { hasRole } = useAuthContext();

 const { openDialog: openConfirmationDialog } = useConfirmationDialog();
 const th = useTheme();

 const [editId, setEditId] = useState<number>();
 const [editName, setEditName] = useState<string>();
 const [hoveredDocument, setHoveredDocument] = useState<number | null>(null);

 const loading = useSelector(
  (state: RootState) => (state as any)[table]?.loading,
 );
 const success = useSelector(
  (state: RootState) => (state as any)[table]?.success,
 );

 const documents = useSelector(
  (state: RootState) => (state as any)[table]?.documents || [],
 );
 const canUploadDocuments = hasRole(table, 'UploadDocuments');
 const canDeleteDocuments = hasRole(table, 'DeleteDocuments');
 const canGetDocuments = hasRole(table, 'GetDocuments');

 const handleFileUpload = async () => {
  if (!canUploadDocuments) return;

  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = true;
  input.onchange = (event) => {
   const { files } = event.target as HTMLInputElement;
   console.log(files);
   if (files) {
    dispatch({
     type: `${table}/uploadItemDocumentsStart`,
     payload: {
      table,
      id,
      files,
     },
    });
   }
  };
  input.click();
 };

 const handleFileDelete = (e: MouseEvent, fileId: number) => {
  if (!canDeleteDocuments) return;

  e.stopPropagation(); // Stops event from bubbling to <a>
  e.preventDefault(); // Prevents <a> navigation (optional, if needed)
  openConfirmationDialog({
   title: t('global.delete'),
   message: t('global.deleteMessage'),
   onConfirm: () => {
    dispatch({
     type: `${table}/deleteItemDocumentStart`,
     payload: {
      table,
      documentId: fileId,
     },
    });
   },
  });
 };

 //  const handleFileNameSave = (document: FileStorageItem) => {
 //   if (editName) {
 //    dispatch(
 //     patchFileStorageDocumentStart({
 //      id: document.Id,
 //      value: editName,
 //      path: '/Name',
 //     }),
 //    );
 //   }
 //  };

 //  const handleFileNameEdit = (
 //   e: MouseEvent | null,
 //   document: FileStorageItem,
 //  ) => {
 //   e?.stopPropagation(); // Stops event from bubbling to <a>
 //   e?.preventDefault(); // Prevents <a> navigation (optional, if needed)

 //   if (editId) {
 //    handleFileNameSave(document);
 //   } else {
 //    setEditId(document.Id);
 //    setEditName(document.Name);
 //   }
 //  };

 useEffect(() => {
  if (id && canGetDocuments) {
   dispatch({
    type: `${table}/getItemDocumentsStart`,
    payload: {
     table,
     id,
    },
   });
  }
 }, [id, table, canGetDocuments]);

 useEffect(() => {
  if (!canGetDocuments) return;

  if (success?.uploadItemDocuments || success?.deleteItemDocument) {
   dispatch({
    type: `${table}/getItemDocumentsStart`,
    payload: {
     table,
     id,
    },
   });
   setEditId(undefined);
   setEditName(undefined);
  }
 }, [success, id, table, canGetDocuments]);

 if (!canGetDocuments) return null;

 return (
  <Box sx={{ mb: 3 }}>
   <Typography sx={titleStyles}>
    {t('entities.documents')}
    {canUploadDocuments && (
     <IconButton
      onClick={() => {
       handleFileUpload();
      }}
     >
      <IconPlus style={{ color: th.palette.kemek.darkestGray }} />
     </IconButton>
    )}
   </Typography>
   {loading?.getItemDocuments ? (
    <CircularProgress />
   ) : !documents.length ? (
    <Box
     sx={{
      textAlign: 'center',
      p: '12px',
      backgroundColor: th.palette.kemek.lightBlue,
     }}
    >
     <Typography variant="body2" color="text.secondary">
      {t('global.noItemsFound')}
     </Typography>
    </Box>
   ) : (
    <Box
     sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '6px',
      mb: 3,
     }}
    >
     {documents.map((document: any) => (
      <Box
       component="a"
       key={document.Id}
       aria-label={document.Name || 'document'}
       title={document.Name || 'document'}
       style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        borderRadius: '4px',
        border: `1px solid ${th.palette.kemek.lightestGray}`,
        textOverflow: 'ellipsis',
        backgroundImage: `url(${`data:image/jpeg;base64,${document.Thumbnail}`})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        width: 62,
        height: 88,
        transition: 'opacity 0.1s ease-in-out',
        cursor: 'pointer',
       }}
       download={document.Name || 'document'}
       target="_blank"
       onClick={() =>
        downloadBase64File(
         document.DocData,
         document.Name,
         document.Type,
         document.Extension,
        )
       }
       rel="noreferrer"
       //    onMouseEnter={() => setHoveredDocument(document.Id)}
       //    onMouseLeave={() => setHoveredDocument(null)}
      >
       {/* <Box
        sx={{
         position: 'absolute',
         top: 0,
         left: 0,
         width: '100%',
         height: '100%',
         background: th.palette.kemek.white,
         transition: 'opacity 0.1s ease-in-out',
         zIndex: 1,
         opacity: 1,
         //  opacity: hoveredDocument === document.Id ? 0.7 : 0,
        }}
       /> */}
       {/* <Typography sx={{}} noWrap>
        {document.Name || 'no name'}
       </Typography> */}
       {/* <img
        src={`data:image/jpeg;base64,${document.Thumbnail}`}
        alt={document.Name || 'no name'}
        style={{ width: 62, height: 88 }}
       /> */}
       {/* <IconButton
        sx={{
         zIndex: 2,
         color: th.palette.kemek.red,
         opacity: hoveredDocument === document.Id ? 1 : 0,
         transition: 'opacity 0.1s ease-in-out',
         backgroundColor: th.palette.kemek.white,
         width: '24px',
         height: '24px',
         '&:hover': {
          backgroundColor: th.palette.kemek.white,
         },
        }}
        onClick={(e) => handleFileDelete(e, document.Id)}
       >
        <IconDelete
         style={{ color: th.palette.kemek.red, width: '16px', height: '16px' }}
        />
       </IconButton> */}
      </Box>
     ))}
    </Box>
   )}
  </Box>
 );
};

export default InfoDocuments;
